import React, { useRef } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { Button } from '../../shadcn/Button';
import ImageGallery from 'react-image-gallery';
import YoutubeVideo, { YoutubeVideoProps } from '../../YoutubeVideo';

type Video = { id: string; thumbnail: string; caption: string };

type YoutubeElement = Element & {
  getYTPlayer: () => Promise<{
    pauseVideo: () => void;
    getPlayerState: () => number;
  }>;
};

export type VideoGalleryProps = {
  value: {
    videos: Video[];
    headline: string;
  };
};

function VideoGallery({ value }: VideoGalleryProps) {
  const galleryRef = useRef<Element & { getCurrentIndex: () => number }>(null);
  const { videos, headline } = value;

  const renderVideo = (video: YoutubeVideoProps) => (
    <div className="video-wrapper">
      <YoutubeVideo id={video.id} title={video.title} />
    </div>
  );

  const stopCurrentVideo = () => {
    const currentIndex = galleryRef.current.getCurrentIndex();
    const youtubeWrapper: YoutubeElement = document.querySelector(
      `lite-youtube[videoid='${value.videos[currentIndex].id}']`
    );
    // the youtube wrapper first shows the image/play button. running getYTPlayer() will start video,
    // so we make sure that the iframe (video) is present first.
    if (youtubeWrapper.querySelector('iframe')) {
      youtubeWrapper.getYTPlayer().then(player => {
        // // Check if is video playing
        if (player.getPlayerState() === 1) {
          player.pauseVideo();
        }
      });
    }
  };
  const getNavItem = (
    onClick: () => void,
    disabled: boolean,
    direction: 'left' | 'right'
  ) => {
    let Icon = MdChevronLeft;

    if (direction === 'right') {
      Icon = MdChevronRight;
    }
    return (
      <Button
        type="button"
        variant="secondary"
        onClick={() => {
          stopCurrentVideo();
          onClick();
        }}
        disabled={disabled}
        className={`z-[1] w-[10%] h-auto aspect-square max-w-20 absolute top-[50%] translate-y-[-50%] p-0 ${
          direction === 'right' ? 'right-2' : 'left-2'
        }`}
        aria-label={direction === 'left' ? 'Previous Video' : 'Next Video'}
      >
        <Icon className="size-full" />
      </Button>
    );
  };
  return (
    <>
      {headline && <h2>{headline}</h2>}
      {!!videos?.length && (
        <ImageGallery
          ref={galleryRef}
          // These special tailwind selectors are used to override the default styles of the image gallery to brand specific colors
          additionalClass="w-full [&_*.active]:!border-primary [&_*.image-gallery-thumbnail:hover:hover]:!border-primary"
          lazyLoad
          items={videos.map(video => ({
            id: video.id,
            thumbnail: video.thumbnail,
            thumbnailAlt: '',
            original: video.thumbnail,
            title: video.caption,
            renderItem: renderVideo,
          }))}
          onThumbnailClick={stopCurrentVideo}
          showPlayButton={false}
          showFullscreenButton={false}
          showThumbnails={videos.length > 1}
          thumbnailPosition="bottom"
          // Render our own nav button because default overflows the video controls.
          renderLeftNav={(onClick, disabled) =>
            getNavItem(onClick, disabled, 'left')
          }
          renderRightNav={(onClick, disabled) =>
            getNavItem(onClick, disabled, 'right')
          }
        />
      )}
    </>
  );
}

export default VideoGallery;
